<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import {
    ChevronDown,
    ChevronsUpDown,
    Briefcase,
    Database,
    Globe,
    LogOut,
    Menu,
    Moon,
    Palette,
    RefreshCw,
    Sun,
    User as UserIcon,
    X,
} from '@lucide/vue';
import { computed, onBeforeMount, onMounted, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button } from '@/components/ui/button/index';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/index';
import { Toaster } from '@/components/ui/sonner';
import CommunicationDetailDialogHost from '@ja/communication/Components/CommunicationDetailDialogHost.vue';
import ConversationMessenger from '@ja/communication/Components/ConversationMessenger.vue';
import MessengerContainer from '@ja/communication/Components/MessengerContainer.vue';
import { useTheme, type ThemeName } from '@ja/core-ui/lib/useTheme';
import ViewerSheet from '@ja/crud/components/ViewerSheet.vue';
import NotificationBell from '@ja/notifications/Components/NotificationBell.vue';
import { setPortalLocale } from '@ja/portal/i18n';
import PwaUpdateBanner from '@ja/portal-employee/components/PwaUpdateBanner.vue';
import { usePwaUpdate } from '@ja/portal-employee/composables/usePwaUpdate';
import { ensureToken, getSessionSettings, setSessionSettings, type Theme } from '@ja/portal-query';

import { ImpersonationBanner, ImpersonationUserPicker } from '../../../Impersonation';
import { useMobileNavbar } from '../composables/useMobileNavbar';
import type { LayoutConfig, LayoutItem, LayoutSection, LayoutTab, SidebarComponent } from '../types/layout';
import { resolveLucideIcon } from '../utils/lucideIcon';
import { isInternalPortalRoute, translateNavLabel } from '../utils/navigation';
import { getSidebarComponent } from '../utils/sidebarComponents';

import MobileFooterNavbar from './MobileFooterNavbar.vue';
import SidebarSectionBody from './SidebarSectionBody.vue';

const props = withDefaults(
    defineProps<{
        /** Show PWA update UI (check-for-updates button, update banner). Only true in employee portal where SW is registered. */
        showPwaUpdates?: boolean;
    }>(),
    { showPwaUpdates: true },
);

// Get shared data from Inertia
const page = usePage();

/**
 * Get data from Inertia props using dotted path notation.
 * @example getDataFromSource('crud.recent_history') -> page.props.crud.recent_history
 */
function getDataFromSource(path: string): unknown {
    const parts = path.split('.');
    let current: unknown = page.props;
    for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
            current = (current as Record<string, unknown>)[part];
        } else {
            return undefined;
        }
    }
    return current;
}

// Side bar state for mobile
const sidebarOpen = ref(false);

// Token gate: fetch token before rendering API-calling components (NotificationBell, page slot, etc.)
// Prevents race where API calls fire before token is available (401s, then retries)
const tokenReady = ref(false);
onBeforeMount(() => {
  void ensureToken().finally(() => {
    tokenReady.value = true; // Proceed even if token fetch failed (user may be redirected to login)
  });
});

const { t: translate, te: translationExists, locale } = useI18n();

// Get settings from Inertia props (shared by HandleSessionSettingsMiddleware)
// Fallback to localStorage for initial load if props are not available
const settingsFromProps = computed(() => {
    return (page.props as any).settings as
        | {
              theme?: string;
              locale?: string;
              colorTheme?: string;
              [key: string]: any;
          }
        | undefined;
});

// Dark mode state - use a ref that can be updated reactively
// Initialize from localStorage first (will be updated by watch when props are available)
const localSettings = getSessionSettings();
const isDark = ref(localSettings.theme === 'dark');

// Apply initial theme to document immediately
if (isDark.value) {
    document.documentElement.classList.add('dark');
} else {
    document.documentElement.classList.remove('dark');
}

function toggleDarkMode(event?: Event) {
    // Prevent dropdown from closing when clicking the toggle button
    if (event) {
        event.stopPropagation();
    }

    const newTheme = isDark.value ? 'light' : 'dark';
    const shouldBeDark = newTheme === 'dark';

    // Update state immediately for reactivity
    isDark.value = shouldBeDark;

    // Apply theme to document
    if (shouldBeDark) {
        document.documentElement.classList.add('dark');
    } else {
        document.documentElement.classList.remove('dark');
    }

    // Update localStorage and sync to cookie
    void setSessionSettings({ theme: newTheme });
}

// Color theme management - sync with sessionSettings
const { theme: colorThemeFromStorage, setTheme: setColorThemeStorage, availableThemes } = useTheme('portal-settings-colorTheme');

// Get colorTheme from Inertia props or localStorage
const colorTheme = computed({
    get: () => {
        const theme = settingsFromProps.value?.colorTheme;
        if (theme) {
            return theme as ThemeName;
        }
        const localSettings = getSessionSettings();
        return (localSettings.colorTheme || 'default') as ThemeName;
    },
    set: (value: ThemeName) => {
        setColorThemeStorage(value);
        void setSessionSettings({ colorTheme: value });
    },
});

// Watch for changes in Inertia props and sync colorTheme
watch(
    () => settingsFromProps.value?.colorTheme,
    (theme) => {
        if (theme && theme !== colorThemeFromStorage.value) {
            setColorThemeStorage(theme as ThemeName);
        }
    },
);

// Language management
const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

const currentLocale = computed(() => locale.value);

function setLocale(code: string): void {
    // Defer to next event loop tick so the dropdown can close before locale change triggers re-render.
    // Prevents reka-ui "Cannot read properties of null (reading 'parentNode')" when
    // portaled content is torn down during the locale switch.
    setTimeout(() => {
        locale.value = code;
        setPortalLocale(code);
        void setSessionSettings({ locale: code as 'en' | 'fr' });
    }, 0);
}

// Watch for changes in Inertia props and apply theme
watch(
    () => settingsFromProps.value?.theme,
    (theme) => {
        // Props take precedence when available
        // On first run, if props aren't available yet, we already initialized from localStorage
        if (theme) {
            const shouldBeDark = theme === 'dark';

            // Update reactive state
            isDark.value = shouldBeDark;

            // Apply theme to document
            if (shouldBeDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }

            // Sync to localStorage if props differ from localStorage
            const localSettings = getSessionSettings();
            if (localSettings.theme !== theme) {
                void setSessionSettings({ theme: theme as Theme });
            }
        }
    },
    { immediate: true },
);

onMounted(() => {
    // Token is fetched in onBeforeMount before API-calling components render

    // Ensure theme is applied (watch should have handled it, but double-check)
    const themeFromProps = settingsFromProps.value?.theme;
    const localSettings = getSessionSettings();
    const themeToUse = themeFromProps || localSettings.theme || 'light';
    const shouldBeDark = themeToUse === 'dark';

    // Update state if needed
    if (isDark.value !== shouldBeDark) {
        isDark.value = shouldBeDark;
    }

    // Apply theme to document
    if (shouldBeDark) {
        document.documentElement.classList.add('dark');
    } else {
        document.documentElement.classList.remove('dark');
    }

    // Sync to localStorage and cookie if props differ from localStorage
    if (themeFromProps && localSettings.theme !== themeFromProps) {
        void setSessionSettings({ theme: themeFromProps as Theme });
    } else if (!themeFromProps && localSettings.theme !== themeToUse) {
        // Ensure localStorage has a theme value
        void setSessionSettings({ theme: themeToUse as Theme });
    }

    // Initialize colorTheme from sessionSettings and sync to useTheme storage
    const colorThemeValue = settingsFromProps.value?.colorTheme || localSettings.colorTheme || 'default';
    if (colorThemeValue !== colorThemeFromStorage.value) {
        setColorThemeStorage(colorThemeValue as ThemeName);
    }
    // Sync colorTheme to localStorage if from Inertia props
    if (settingsFromProps.value?.colorTheme && localSettings.colorTheme !== settingsFromProps.value.colorTheme) {
        void setSessionSettings({ colorTheme: settingsFromProps.value.colorTheme });
    }

    const onSwMessage = (event: MessageEvent) => {
        if (event.data?.type === 'portal-push-navigate' && typeof event.data.url === 'string') {
            router.visit(event.data.url);
        }
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
});

// PWA update check (only when SW supported and in employee portal)
const showPwaUpdates = computed(() => props.showPwaUpdates);
const hasServiceWorker = computed(
    () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
);
const { checkForUpdates } = usePwaUpdate();
const isCheckingUpdates = ref(false);
async function handleCheckForUpdates() {
    if (!hasServiceWorker.value) return;
    isCheckingUpdates.value = true;
    try {
        await checkForUpdates();
    } finally {
        isCheckingUpdates.value = false;
    }
}

// Get user from page props
interface AuthUser {
    id: string;
    full_name?: string;
    email?: string;
}

const user = computed(() => (page.props as any).auth?.user as AuthUser | undefined);

// Impersonation state shared by the ShareImpersonationState middleware.
interface ImpersonationShared {
    isImpersonating: boolean;
    target?: { name?: string; email?: string } | null;
    impersonator?: { id: string; name?: string; email?: string } | null;
}

const impersonation = computed(() => (page.props as any).impersonation as ImpersonationShared | undefined);

// The account / user menu always reflects the REAL logged-in account, even
// while impersonating: its actions (account security, logout) operate on that
// account. The impersonated identity is surfaced by the impersonation banner
// and the indicator line inside the menu.
const accountUser = computed<AuthUser | undefined>(() => {
    const imp = impersonation.value;
    if (imp?.isImpersonating && imp.impersonator) {
        return {
            id: imp.impersonator.id,
            full_name: imp.impersonator.name,
            email: imp.impersonator.email,
        };
    }
    return user.value;
});

// Get layout configuration from shared data
const layoutConfig = computed((): LayoutConfig => {
    const config = (page.props as any).layout as LayoutConfig | undefined;
    return config ?? { sidebar: [], userMenu: [] };
});

// Branding - translate key from backend or use fallback
const branding = computed(() => {
    const config = layoutConfig.value.branding;
    if (config?.titleKey) {
        return {
            title: translate(config.titleKey),
            icon: config.icon ?? 'Database',
        };
    }
    return {
        title: config?.title ?? translate('portal.title'),
        icon: config?.icon ?? 'Database',
    };
});

// Dashboard configuration - translate key from backend or use fallback
const dashboard = computed(() => {
    const config = layoutConfig.value.dashboard;
    if (config?.labelKey) {
        return {
            label: translate(config.labelKey),
            route: config.route ?? '/',
            icon: config.icon ?? 'Home',
        };
    }
    return {
        label: config?.label ?? translate('portal.dashboard'),
        route: config?.route ?? '/',
        icon: config?.icon ?? 'Home',
    };
});

// Get translated label for a layout item
function getTranslatedLabel(item: LayoutItem, type: 'singular' | 'plural' = 'plural'): string {
    if (item.translationPrefix) {
        const key = `models.${item.translationPrefix}.${type}`;
        if (translationExists(key)) {
            return translate(key);
        }
    }
    if (item.label && translationExists(item.label)) {
        return translate(item.label);
    }
    return item.label;
}

// Layout sections sorted by priority with translated titles and items sorted alphabetically
function processSections(sections: LayoutSection[]): LayoutSection[] {
    return [...sections]
        .map((section) => ({
            ...section,
            // Translate titleKey if present, otherwise use title
            title: section.titleKey ? translate(section.titleKey) : (section.title ?? ''),
            // Sort items alphabetically by translated label
            items: [...(section.items ?? [])].sort((a, b) => {
                const labelA = getTranslatedLabel(a).toLowerCase();
                const labelB = getTranslatedLabel(b).toLowerCase();
                return labelA.localeCompare(labelB);
            }),
        }))
        .sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
}

const sidebarSections = computed((): LayoutSection[] => {
    return processSections(layoutConfig.value.sidebar ?? []);
});

const userMenuSections = computed((): LayoutSection[] => {
    return processSections(layoutConfig.value.userMenu ?? []);
});

/**
 * Distinct tabs derived from sidebar sections that declare a `tab`.
 * Tab order = explicit `tab.priority` if any section provides one, else the
 * lowest section `priority` carrying that tab key.
 */
type ResolvedTab = LayoutTab & { resolvedLabel: string; minSectionPriority: number };

const sidebarTabs = computed((): ResolvedTab[] => {
    const byKey = new Map<string, ResolvedTab>();
    for (const section of sidebarSections.value) {
        const tab = section.tab;
        if (!tab?.key) continue;
        const existing = byKey.get(tab.key);
        const sectionPriority = section.priority ?? 100;
        if (!existing) {
            byKey.set(tab.key, {
                key: tab.key,
                label: tab.label,
                labelKey: tab.labelKey,
                priority: tab.priority,
                resolvedLabel: tab.labelKey && translationExists(tab.labelKey)
                    ? translate(tab.labelKey)
                    : (tab.label ?? tab.key),
                minSectionPriority: sectionPriority,
            });
        } else {
            // Later sections may carry a more specific label/priority — keep the
            // first non-empty value, but track the lowest section priority for
            // implicit ordering when no explicit tab.priority is set.
            if (existing.priority === undefined && tab.priority !== undefined) {
                existing.priority = tab.priority;
            }
            if (!existing.label && tab.label) existing.label = tab.label;
            if (!existing.labelKey && tab.labelKey) {
                existing.labelKey = tab.labelKey;
                if (translationExists(tab.labelKey)) {
                    existing.resolvedLabel = translate(tab.labelKey);
                }
            }
            if (sectionPriority < existing.minSectionPriority) {
                existing.minSectionPriority = sectionPriority;
            }
        }
    }
    return [...byKey.values()].sort((a, b) => {
        const pa = a.priority ?? a.minSectionPriority;
        const pb = b.priority ?? b.minSectionPriority;
        return pa - pb;
    });
});

const ACTIVE_TAB_STORAGE_KEY = 'jsw.sidebar.activeTab';

function readPersistedTab(): string | null {
    try {
        return window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    } catch {
        return null;
    }
}

const activeTabKey = ref<string | null>(readPersistedTab());

// Reconcile persisted/active tab against the currently available tabs:
// if the stored tab no longer exists (permission change, app switch), fall
// back to the first available tab; if no tabs exist, clear it.
watch(
    sidebarTabs,
    (tabs) => {
        if (tabs.length === 0) {
            activeTabKey.value = null;
            return;
        }
        const stillPresent = tabs.some((t) => t.key === activeTabKey.value);
        if (!stillPresent) {
            activeTabKey.value = tabs[0].key;
        }
    },
    { immediate: true },
);

watch(activeTabKey, (key) => {
    try {
        if (key) {
            window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, key);
        } else {
            window.localStorage.removeItem(ACTIVE_TAB_STORAGE_KEY);
        }
    } catch {
        // localStorage may be unavailable (private mode, quota) — ignore
    }
});

/**
 * A section has visible content when:
 *  - it has at least one item after permission filtering, OR
 *  - it has a dataSource that currently resolves to a non-empty array.
 *
 * Sections that fail both are hidden — e.g. Recently Opened with no entries.
 */
function sectionHasContent(section: LayoutSection): boolean {
    if (section.dataSource) {
        const data = getDataFromSource(section.dataSource);
        return Array.isArray(data) && data.length > 0;
    }
    return (section.items?.length ?? 0) > 0;
}

/**
 * Sidebar split into two render buckets:
 *  - `visibleSidebarSections` — normal flow, ordered by priority, with tab
 *    strip injected before the first tabbed section.
 *  - `pinnedBottomSections` — flushed to the foot of the nav via `mt-auto`,
 *    always visible regardless of active tab.
 *
 * Within each bucket: tabbed sections are dropped unless their tab is active,
 * and empty data-source sections are dropped entirely (see `sectionHasContent`).
 */
const visibleSidebarSections = computed((): LayoutSection[] => {
    const filtered = sidebarSections.value.filter((section) => {
        if (section.pinBottom) return false;
        if (section.tab?.key && section.tab.key !== activeTabKey.value) return false;
        return sectionHasContent(section);
    });
    return [...filtered].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
});

const pinnedBottomSections = computed((): LayoutSection[] => {
    const filtered = sidebarSections.value.filter((section) => {
        if (!section.pinBottom) return false;
        if (section.tab?.key && section.tab.key !== activeTabKey.value) return false;
        return sectionHasContent(section);
    });
    return [...filtered].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
});

// Index of the first tabbed section in `visibleSidebarSections` — the template
// renders the tab strip right before that index. `-1` = no tabbed section to
// anchor to, so no strip.
const tabStripInsertIndex = computed((): number => {
    if (sidebarTabs.value.length < 2) return -1;
    return visibleSidebarSections.value.findIndex((s) => !!s.tab?.key);
});

// Sidebar components from packages
const sidebarComponents = computed((): Array<SidebarComponent & { resolvedComponent?: Component }> => {
    const components = layoutConfig.value.sidebarComponents ?? [];
    return components
        .map((comp) => ({
            ...comp,
            resolvedComponent: getSidebarComponent(comp.component),
        }))
        .filter((comp) => comp.resolvedComponent !== undefined)
        .sort((a, b) => a.priority - b.priority);
});

// Current URL for active state detection
const currentUrl = computed(() => page.url);

// Check if on dashboard
const isOnDashboard = computed(() => {
    const dashboardRoute = dashboard.value.route;
    return currentUrl.value === dashboardRoute || currentUrl.value === dashboardRoute + '/';
});

// Get the current portal's route prefix for internal/external link detection.
// Uses explicit routePrefix when set (decoupled from dashboard link destination).
const portalRoutePrefix = computed(() => {
    const prefix = layoutConfig.value.dashboard?.routePrefix ?? dashboard.value.route;
    return prefix === '/' ? '' : prefix;
});

// Whether the dashboard link itself is external (points to a different portal)
const isDashboardExternal = computed(() => !isInternalRoute(dashboard.value.route));

function isInternalRoute(route: string, item?: LayoutItem): boolean {
    return isInternalPortalRoute(portalRoutePrefix.value, route, item);
}

function translateLabel(item: LayoutItem, type: 'singular' | 'plural' = 'plural'): string {
    return translateNavLabel(translate, translationExists, item, type);
}

// Mobile footer navbar (user-configurable, < lg only)
const { settings: mobileNavbarSettings } = useMobileNavbar();
const mobileNavbarEnabled = computed(() => mobileNavbarSettings.value.enabled);
</script>

<template>
    <!-- Impersonation Banner (teleported to body) -->
    <ImpersonationBanner />

    <!-- Toast notifications provider -->
    <Toaster :theme="isDark ? 'dark' : 'light'" position="top-center" rich-colors />
    <ViewerSheet />

    <div class="bg-background min-h-screen">
        <!-- Mobile sidebar backdrop -->
        <div v-if="sidebarOpen" class="impersonation-offset-backdrop fixed inset-0 z-40 bg-black/50 lg:hidden" @click="sidebarOpen = false" />

        <!-- Sidebar -->
        <aside
            :class="[
                'impersonation-offset-sidebar bg-sidebar fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r transition-transform duration-300 lg:translate-x-0',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            ]"
        >
            <!-- Logo -->
            <div class="flex h-16 items-center gap-3 border-b px-6">
                <Database class="text-primary size-6" />
                <span class="text-lg font-semibold">{{ branding.title }}</span>
                <Button variant="ghost" size="icon" class="ml-auto lg:hidden" @click="sidebarOpen = false">
                    <X class="size-5" />
                </Button>
            </div>

            <!-- Navigation -->
            <nav class="flex flex-1 flex-col space-y-1 overflow-y-auto p-4">
                <!-- Dashboard Link (external = full reload, internal = Inertia) -->
                <a
                    v-if="isDashboardExternal"
                    :href="dashboard.route"
                    class="text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors"
                >
                    <Briefcase class="size-4" />
                    {{ dashboard.label }}
                </a>
                <Link
                    v-else
                    :href="dashboard.route"
                    :class="[
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        isOnDashboard ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    ]"
                >
                    <Briefcase class="size-4" />
                    {{ dashboard.label }}
                </Link>

                <!-- Dynamic Sections (untabbed first, then tabbed; tab strip injected at the boundary) -->
                <template v-for="(section, sectionIndex) in visibleSidebarSections" :key="section.id ?? section.title">
                    <!-- Tab strip anchored at the first tabbed section.
                         Negative horizontal margin (`-mx-4 px-4`) makes the
                         bottom border span the full sidebar width so the strip
                         reads as part of the panel instead of a floating bar. -->
                    <div
                        v-if="sectionIndex === tabStripInsertIndex"
                        role="tablist"
                        :aria-label="translate('portal.sidebar_tabs')"
                        class="border-border/60 -mx-4 mt-4 flex items-end gap-4 border-b px-4 text-sm"
                    >
                        <button
                            v-for="tab in sidebarTabs"
                            :key="tab.key"
                            type="button"
                            role="tab"
                            :aria-selected="tab.key === activeTabKey"
                            :class="[
                                '-mb-px border-b-2 pb-2 pt-1 transition-colors',
                                tab.key === activeTabKey
                                    ? 'border-foreground text-foreground font-medium'
                                    : 'text-muted-foreground hover:text-foreground border-transparent',
                            ]"
                            @click="activeTabKey = tab.key"
                        >
                            {{ tab.resolvedLabel }}
                        </button>
                    </div>

                    <div class="pt-4">
                        <SidebarSectionBody :section="section" :route-prefix="portalRoutePrefix" />
                    </div>
                </template>

                <!-- Dynamic Sidebar Components from Packages -->
                <div v-for="comp in sidebarComponents" :key="comp.id" class="pt-2">
                    <component :is="comp.resolvedComponent" v-bind="comp.props" />
                </div>

                <!-- Pinned-to-bottom sections (e.g. Recently Opened). `mt-auto`
                     on the wrapper pushes this block to the foot of the flex
                     column regardless of how much content sits above. -->
                <div v-if="pinnedBottomSections.length > 0" class="mt-auto space-y-1 pt-4">
                    <div v-for="section in pinnedBottomSections" :key="section.id ?? section.title">
                        <SidebarSectionBody :section="section" :route-prefix="portalRoutePrefix" />
                    </div>
                </div>
            </nav>

            <!-- Footer: account menu (signed-in identity is the trigger) -->
            <div class="border-t p-4">
                <DropdownMenu v-if="user">
                    <DropdownMenuTrigger as-child>
                        <button
                            type="button"
                            class="hover:bg-muted focus-visible:ring-ring flex w-full items-center gap-3 rounded-lg p-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2"
                        >
                            <div class="bg-muted flex size-9 items-center justify-center rounded-full">
                                <UserIcon class="text-muted-foreground size-4" />
                            </div>
                            <div class="flex-1 overflow-hidden">
                                <p class="truncate text-sm font-medium">
                                    {{ user.full_name ?? translate('portal.user') }}
                                </p>
                                <p class="text-muted-foreground truncate text-xs">
                                    {{ user.email }}
                                </p>
                            </div>
                            <ChevronsUpDown class="text-muted-foreground size-4 shrink-0" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="top" align="start" class="w-64">
                        <div class="px-2 py-1.5">
                            <p class="text-sm font-medium">{{ accountUser?.full_name }}</p>
                            <p class="text-muted-foreground text-xs">{{ accountUser?.email }}</p>
                            <!-- Stays bound to the real account; indicates active impersonation -->
                            <p
                                v-if="impersonation?.isImpersonating"
                                class="mt-1.5 flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-500"
                            >
                                <UserIcon class="size-3 shrink-0" />
                                <span class="truncate">{{
                                    translate('portal.impersonation.banner_message', {
                                        name:
                                            impersonation?.target?.name ??
                                            translate('portal.impersonation.unknown_user'),
                                    })
                                }}</span>
                            </p>
                        </div>
                        <DropdownMenuSeparator />

                        <!-- Settings Row -->
                        <div class="flex items-center gap-2 px-2 py-1.5">
                            <!-- Dark Mode Toggle Button -->
                            <Button variant="ghost" size="icon" @click="toggleDarkMode($event)">
                                <Moon v-if="isDark" class="size-4" />
                                <Sun v-else class="size-4" />
                            </Button>

                            <!-- Language Dropdown -->
                            <DropdownMenu>
                                <DropdownMenuTrigger as-child>
                                    <Button variant="outline" class="h-8 flex-1 gap-2 px-3">
                                        <Globe class="size-4" />
                                        <span class="text-sm font-medium">{{ currentLocale.toUpperCase() }}</span>
                                        <ChevronDown class="ml-auto size-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuLabel class="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                        {{ translate('portal.language') }}
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                        v-for="lang in languages"
                                        :key="lang.code"
                                        class="cursor-pointer px-2 py-1.5"
                                        @click="setLocale(lang.code)"
                                    >
                                        <div class="flex items-center gap-2">
                                            <span>{{ lang.flag }}</span>
                                            <span>{{ lang.name }}</span>
                                        </div>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        <!-- Color Theme Dropdown -->
                        <div class="px-2 py-1.5">
                            <DropdownMenu>
                                <DropdownMenuTrigger as-child>
                                    <Button variant="outline" class="h-8 w-full justify-start gap-2 px-3">
                                        <Palette class="size-4" />
                                        <span class="text-sm font-medium">{{ translate('portal.color_theme') }}</span>
                                        <ChevronDown class="ml-auto size-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuLabel class="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                        {{ translate('portal.color_theme') }}
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                        v-for="theme in availableThemes"
                                        :key="theme"
                                        class="cursor-pointer px-2 py-1.5"
                                        @click="colorTheme = theme"
                                    >
                                        {{ theme.charAt(0).toUpperCase() + theme.slice(1) }}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        <!-- Check for updates (PWA) - only in employee portal where SW is registered -->
                        <div v-if="showPwaUpdates && hasServiceWorker" class="px-2 py-1.5">
                            <Button
                                variant="outline"
                                class="h-8 w-full justify-start gap-2 px-3"
                                :disabled="isCheckingUpdates"
                                @click="handleCheckForUpdates"
                            >
                                <RefreshCw class="size-4" :class="{ 'animate-spin': isCheckingUpdates }" />
                                <span class="text-sm">{{ translate('portal.pwa.check_for_updates') }}</span>
                            </Button>
                        </div>

                        <DropdownMenuSeparator />

                        <!-- Impersonation User Picker (only shows if user can impersonate) -->
                        <ImpersonationUserPicker />

                        <!-- Dynamic User Menu Sections -->
                        <template v-for="section in userMenuSections" :key="section.id">
                            <div v-if="section.title" class="text-muted-foreground px-2 py-1 text-xs font-semibold">
                                {{ section.title }}
                            </div>
                            <template v-for="item in section.items" :key="item.key">
                            <!-- Logout: POST request to clear session -->
                            <DropdownMenuItem v-if="item.key === 'logout'" as-child>
                                <button
                                    type="button"
                                    class="flex w-full cursor-pointer items-center"
                                    @click="() => router.post('/logout')"
                                >
                                    <LogOut class="mr-2 size-4" />
                                    {{ translateLabel(item) }}
                                </button>
                            </DropdownMenuItem>
                            <DropdownMenuItem v-else-if="item.route" as-child>
                                <!-- Internal route: use Inertia Link -->
                                <Link v-if="isInternalRoute(item.route, item)" :href="item.route" class="flex cursor-pointer items-center">
                                    <component
                                        :is="resolveLucideIcon(item.icon)"
                                        class="mr-2 size-4"
                                    />
                                    {{ translateLabel(item) }}
                                </Link>
                                <!-- External route: use <a> for full page reload -->
                                <a v-else :href="item.route" class="flex cursor-pointer items-center">
                                    <component
                                        :is="resolveLucideIcon(item.icon)"
                                        class="mr-2 size-4"
                                    />
                                    {{ translateLabel(item) }}
                                </a>
                            </DropdownMenuItem>
                            </template>
                            <DropdownMenuSeparator />
                        </template>

                        <!-- Default Logout Link (if not provided by any package) -->
                        <DropdownMenuItem as-child v-if="userMenuSections.length === 0">
                            <a href="/" class="flex cursor-pointer items-center">
                                <LogOut class="mr-2 size-4" />
                                {{ translate('portal.back_to_app') }}
                            </a>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </aside>

        <!-- Main content (token gate: only render API-calling components after token is ready) -->
        <div
            :class="[
                'impersonation-offset-content flex min-h-screen flex-col lg:pl-64',
                mobileNavbarEnabled && 'pb-[calc(3.75rem_+_env(safe-area-inset-bottom))] lg:pb-0',
            ]"
        >
            <template v-if="tokenReady">
            <!-- Top bar -->
            <header
                class="impersonation-offset-header bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-30 flex min-w-0 h-16 items-center gap-2 border-b px-3 backdrop-blur sm:gap-4 sm:px-6"
            >
                <Button variant="ghost" size="icon" class="lg:hidden" @click="sidebarOpen = true">
                    <Menu class="size-5" />
                </Button>

                <!-- Header Portal -->
                <div id="header-portal" class="flex min-w-0 flex-1 items-center" />

                <!-- PWA update banner + portal-specific header (e.g., CRUD search, offline status) -->
                <div class="flex min-w-0 shrink items-center gap-2">
                    <PwaUpdateBanner v-if="showPwaUpdates" />
                    <slot name="header-search" />
                </div>

                <!-- Actions -->
                <div class="flex shrink-0 items-center gap-2">
                    <!-- Conversations -->
                    <ConversationMessenger v-if="user" />

                    <!-- Notifications -->
                    <NotificationBell v-if="user" />
                </div>
            </header>

            <!-- Page content -->
            <main class="flex-1 p-4">
                <slot />
            </main>

            <!-- Footer Portal (sits above the mobile navbar when it is enabled) -->
            <div
                id="footer-portal"
                :class="[
                    'bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky z-20 flex items-center border-t px-6 py-4 backdrop-blur empty:hidden',
                    mobileNavbarEnabled
                        ? 'bottom-[calc(3.75rem_+_env(safe-area-inset-bottom))] lg:bottom-0'
                        : 'bottom-0',
                ]"
            />
            </template>
            <div v-else class="flex flex-1 items-center justify-center p-8">
                <RefreshCw class="text-muted-foreground size-8 animate-spin" />
            </div>
        </div>

        <!-- Mobile footer navbar (user-configurable via Settings) -->
        <MobileFooterNavbar v-if="tokenReady" :route-prefix="portalRoutePrefix" />

        <!-- Portal-specific overlays (e.g., CRUD global search dialog) -->
        <slot v-if="tokenReady" name="overlays" />

        <!-- Messenger Popup Containers -->
        <CommunicationDetailDialogHost v-if="user" />
        <MessengerContainer v-if="user" :user-id="user.id" />
    </div>
</template>

<style>
/* Offset layout elements when impersonation banner is active */
body.body--impersonating .impersonation-offset-sidebar {
    top: var(--portal-banner-height, 0);
    height: calc(100vh - var(--portal-banner-height, 0));
}

body.body--impersonating .impersonation-offset-header {
    top: var(--portal-banner-height, 0);
}

body.body--impersonating .impersonation-offset-backdrop {
    top: var(--portal-banner-height, 0);
}

body.body--impersonating .impersonation-offset-content {
    padding-top: var(--portal-banner-height, 0);
}
</style>
